import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTheme } from '../../contexts/ThemeContext'

const ModernChart = ({ type, labels, dataValues, labelTitle }) => {
  const { isDark } = useTheme()

  const getColors = () => {
    if (isDark) {
      return {
        text: 'hsl(210, 20%, 95%)',
        grid: 'hsl(215, 25%, 22%)',
        background: 'hsl(215, 30%, 16%)',
        border: 'hsl(215, 25%, 22%)'
      }
    }
    return {
      text: '#4E4B59',
      grid: '#E0E0E0',
      background: '#FFFFFF',
      border: '#D3D2D7'
    }
  }

  const colors = getColors()

  const chartColors = [
    '#E9D3FB',
    '#D6DAFF',
    '#FFF9D7',
    '#DCDCDC',
    '#FFF6D7',
    '#FFE7D6',
    '#D3FBE5',
    '#FBEDD3',
    '#F8D0D0',
    '#D6FBD3',
    '#FFD7F4',
    '#FFD7F4'
  ]

  const getBarOptions = () => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'Inter, sans-serif'
      },
      borderRadius: 16,
      spacing: [20, 20, 20, 20]
    },
    title: {
      text: labelTitle || '',
      style: {
        color: colors.text,
        fontSize: '18px',
        fontWeight: '600'
      },
      margin: 20
    },
    xAxis: {
      categories: labels,
      labels: {
        style: {
          color: colors.text,
          fontSize: '12px',
          fontWeight: '500'
        },
        rotation: -45,
        align: 'right',
        useHTML: true
      },
      gridLineColor: colors.grid,
      lineColor: colors.border,
      tickColor: colors.border
    },
    yAxis: {
      title: {
        text: 'Quantidade',
        style: {
          color: colors.text,
          fontSize: '14px',
          fontWeight: '600'
        }
      },
      labels: {
        style: {
          color: colors.text,
          fontSize: '12px'
        }
      },
      gridLineColor: colors.grid
    },
    series: [{
      name: labelTitle || 'Demandas',
      data: dataValues,
      colorByPoint: true,
      borderRadius: 8,
      borderWidth: 0,
      dataLabels: {
        enabled: true,
        style: {
          color: colors.text,
          fontSize: '12px',
          fontWeight: '600',
          textOutline: 'none'
        }
      }
    }],
    colors: chartColors,
    legend: {
      enabled: false
    },
    plotOptions: {
      column: {
        borderRadius: 8,
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.1
      }
    },
    tooltip: {
      backgroundColor: colors.background,
      borderColor: colors.border,
      borderRadius: 8,
      style: {
        color: colors.text,
        fontSize: '13px'
      },
      shadow: {
        color: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    credits: {
      enabled: false
    }
  })

  const getDoughnutOptions = () => ({
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'Inter, sans-serif'
      },
      borderRadius: 16,
      spacing: [20, 20, 20, 20]
    },
    title: {
      text: '',
      style: {
        color: colors.text
      }
    },
    tooltip: {
      backgroundColor: colors.background,
      borderColor: colors.border,
      borderRadius: 8,
      style: {
        color: colors.text,
        fontSize: '13px'
      },
      pointFormat: '<b>{point.name}</b>: {point.y} ({point.percentage:.1f}%)',
      shadow: {
        color: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b><br/>{point.y} ({point.percentage:.1f}%)',
          style: {
            color: colors.text,
            fontSize: '12px',
            fontWeight: '500',
            textOutline: 'none'
          },
          distance: 20
        },
        showInLegend: true,
        borderRadius: 8,
        borderWidth: 0,
        innerSize: '40%',
        states: {
          hover: {
            brightness: 0.1
          }
        }
      }
    },
    colors: chartColors,
    series: [{
      name: 'Documentos',
      data: labels.map((label, index) => ({
        name: label,
        y: dataValues[index] || 0
      }))
    }],
    legend: {
      itemStyle: {
        color: colors.text,
        fontSize: '12px',
        fontWeight: '500'
      },
      itemHoverStyle: {
        color: colors.text
      },
      itemHiddenStyle: {
        color: colors.grid
      }
    },
    credits: {
      enabled: false
    }
  })

  const getLineOptions = () => ({
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'Inter, sans-serif'
      },
      borderRadius: 16,
      spacing: [20, 20, 20, 20]
    },
    title: {
      text: '',
      style: {
        color: colors.text
      }
    },
    xAxis: {
      categories: labels,
      labels: {
        style: {
          color: colors.text,
          fontSize: '12px',
          fontWeight: '500'
        }
      },
      gridLineColor: colors.grid,
      lineColor: colors.border,
      tickColor: colors.border
    },
    yAxis: {
      title: {
        text: 'Valor (BNZ)',
        style: {
          color: colors.text,
          fontSize: '14px',
          fontWeight: '600'
        }
      },
      labels: {
        style: {
          color: colors.text,
          fontSize: '12px'
        }
      },
      gridLineColor: colors.grid
    },
    series: [
      {
        name: 'Reserva',
        data: dataValues[0] || [],
        color: '#7683F6',
        lineWidth: 3,
        marker: {
          radius: 6,
          symbol: 'circle',
          fillColor: '#7683F6',
          lineWidth: 2,
          lineColor: '#FFFFFF'
        },
        states: {
          hover: {
            lineWidth: 4
          }
        }
      },
      {
        name: 'Crédito',
        data: dataValues[1] || [],
        color: '#44E47A',
        lineWidth: 3,
        marker: {
          radius: 6,
          symbol: 'circle',
          fillColor: '#44E47A',
          lineWidth: 2,
          lineColor: '#FFFFFF'
        },
        states: {
          hover: {
            lineWidth: 4
          }
        }
      },
      {
        name: 'Débito',
        data: dataValues[2] || [],
        color: '#F14F5C',
        lineWidth: 3,
        marker: {
          radius: 6,
          symbol: 'circle',
          fillColor: '#F14F5C',
          lineWidth: 2,
          lineColor: '#FFFFFF'
        },
        states: {
          hover: {
            lineWidth: 4
          }
        }
      }
    ],
    legend: {
      itemStyle: {
        color: colors.text,
        fontSize: '12px',
        fontWeight: '500'
      },
      itemHoverStyle: {
        color: colors.text
      }
    },
    plotOptions: {
      line: {
        marker: {
          enabled: true,
          states: {
            hover: {
              enabled: true,
              radius: 8
            }
          }
        }
      }
    },
    tooltip: {
      backgroundColor: colors.background,
      borderColor: colors.border,
      borderRadius: 8,
      style: {
        color: colors.text,
        fontSize: '13px'
      },
      shadow: {
        color: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'
      },
      shared: true
    },
    credits: {
      enabled: false
    }
  })

  const getOptions = () => {
    switch (type) {
      case 'Bar':
        return getBarOptions()
      case 'Dognhut':
        return getDoughnutOptions()
      case 'Line':
        return getLineOptions()
      default:
        return getBarOptions()
    }
  }

  const options = getOptions()

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', width: '100%' } }}
    />
  )
}

export default ModernChart
